import { useMemo, useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  FileInput,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  RISK_MADDE_EKSIK_ETIKETI,
  RISK_METODOLOJILERI,
  RISK_METODOLOJI_ETIKETI,
  RISK_SEVIYELERI,
  RISK_SEVIYESI_ETIKETI,
  riskRutinEtiketi,
  riskSkoruTuret,
} from '@isg/validation';
import type {
  RiskKapsamTipi,
  RiskMetodolojisi,
  RiskSeviyesi,
  riskAssessmentDetailSchema,
  riskAssessmentOptionsSchema,
} from '@isg/validation';
import type { z } from 'zod';
import {
  useAksiyonAc,
  useCiktiIndir,
  useRiskDegerlendirmeKaydet,
  useRiskDegerlendirmeSil,
  useRiskDegerlendirmesi,
  useRiskEkipUyesiCikar,
  useRiskEkipUyesiEkle,
  useRiskMaddesiKaydet,
  useRiskMaddesiSil,
  useRiskMaddeYukle,
  useRiskBantTablosu,
  useRiskOptions,
  useRiskSurumDondur,
} from '../api/queries';
import type { RiskMaddeSuzgeciUi } from '../api/queries';
import { AksiyonFormAlanlari } from './AksiyonlarScreen';
import { QueryStatus } from './parts';
import {
  Bolum,
  CokenBolum,
  KapiSebebi,
  KayitDetayi,
  Rozet,
  SatirEylemleri,
  Sayfalama,
  SolukSatir,
  SuzgecGostergesi,
} from '../sozlesme';
import { YazamamaSebebi } from '../sozlesme/YazamamaSebebi';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { KapsamMetni, SeviyeRozeti } from './risk-parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster } from '../lib/format';

/**
 * Risk değerlendirmesi SAYFASI — hem yeni kayıt hem mevcut kayıt (F3B·S9 —
 * Kapsam §16.12; F4·S5·B5'te sözleşmeye hizalandı; ✏️ **madde 220'de
 * YENİDEN KURULDU — Kapsam §16.12/16**).
 *
 * > **Tek cümle: HEDEF BİR NİYETTİR — KAPIYI MEVCUT SKOR AÇAR.**
 *
 * ÜÇ KURAL EKRANDA GÖRÜNÜR AMA EKRANDA YAZILMAZ:
 *
 * 1. **Düğme görünürlüğü SUNUCUDAN** (`yetkiler` — S4 deseni).
 * 2. **Skor ve bant TÜRETİLİR** — ekran hesap yapmaz; MEVCUT ve HEDEF skorun
 *    değer/bant hücreleri sunucunun verdiğini basar (hedef kolonda bile
 *    değildir, okumada türetilir). Düzenlenen satırda bu hücreler boştur:
 *    değer kaydedince hesaplanır.
 * 3. **R5 UYARI/KAPI MEVCUT SKORU OKUR** — hedefi düşük bir madde yüksek
 *    bantta aksiyonsuzsa uyarı yine çıkar (R2).
 *
 * MADDE 220 YÜZEYİ:
 *
 * - **R6 — MODAL YOK:** *"Yeni değerlendirme"* bu sayfayı BOŞ açar; künye
 *   üstte. ⚠️ Yöntem kayıtta donduğu ve ölçek ondan geldiği için **künye
 *   kaydedilmeden madde satırı pasiftir ve SEBEBİ satırda yazar** (§2.40).
 *   Kaydedince sayfa kaydın adresine geçer (yenileme yok).
 * - **R7/R8 — TEK SATIR, YATAY KAYDIRMA:** ürün sahibinin kolon sırası;
 *   faaliyet + tehlike sola sabit. İki kademeli satır YOK. Az kullanılan
 *   bağlar (lokasyon · ekipman · "Diğer" metni) düzenleme satırının ikinci
 *   katmanında kalır (P1-b).
 * - **R9 — ETİKETLİ ÖLÇEK:** açılır liste değer + anlamı birlikte gösterir.
 * - **R10 — skor ve bant AYRI hücre.**
 * - **R11 — P1-c TAMAMLANDI:** Vazgeç kaydet düğmesinin yanında; düzenlenen
 *   satırda kaydedilmemiş değişiklik varken başka satırı düzenlemeye açmak,
 *   sayfa ya da süzgeç değiştirmek ONAY sorar. Eskiden değişiklik uyarısız
 *   atılıyordu — sessiz veri kaybı.
 * - **R12 — üst tetikleyici:** sayfalanan madde listesinde soluk satırı başa
 *   alır (P1-a şerhi; iki soluk satır çizilmez).
 * - **Sorumlu + termin AKSİYONDAN** (R7 kararı) — satırda salt okunurdur.
 */
type Detay = z.output<typeof riskAssessmentDetailSchema>;
type RiskMaddesi = Detay['maddeler'][number];
type Options = z.output<typeof riskAssessmentOptionsSchema>;
type Eksen = 'olasilik' | 'frekans' | 'siddet';

interface MaddeTaslagi {
  faaliyet: string;
  tehlikeRefId: string | null;
  tehlikeDigerMetin: string;
  riskRefId: string | null;
  riskDigerMetin: string;
  rutin: 'rutin' | 'rutin_degil' | null;
  mevcutOnlem: string;
  ilgiliMevzuat: string;
  olasilik: number | '';
  frekans: number | '';
  siddet: number | '';
  /** Yüzey adı "planlanan önlem" — kolon `ilave_onlem`. */
  ilaveOnlem: string;
  hedefOlasilik: number | '';
  hedefFrekans: number | '';
  hedefSiddet: number | '';
  locationNodeId: string | null;
  equipmentId: string | null;
}

const BOS_MADDE: MaddeTaslagi = {
  faaliyet: '',
  tehlikeRefId: null,
  tehlikeDigerMetin: '',
  riskRefId: null,
  riskDigerMetin: '',
  rutin: null,
  mevcutOnlem: '',
  ilgiliMevzuat: '',
  olasilik: '',
  frekans: '',
  siddet: '',
  ilaveOnlem: '',
  hedefOlasilik: '',
  hedefFrekans: '',
  hedefSiddet: '',
  locationNodeId: null,
  equipmentId: null,
};

function maddedenTaslak(m: RiskMaddesi): MaddeTaslagi {
  return {
    faaliyet: m.faaliyet,
    tehlikeRefId: m.tehlikeRefId,
    tehlikeDigerMetin: m.tehlikeDigerMetin ?? '',
    riskRefId: m.riskRefId,
    riskDigerMetin: m.riskDigerMetin ?? '',
    rutin: m.rutin === null ? null : m.rutin ? 'rutin' : 'rutin_degil',
    mevcutOnlem: m.mevcutOnlem ?? '',
    ilgiliMevzuat: m.ilgiliMevzuat ?? '',
    olasilik: m.olasilik ?? '',
    frekans: m.frekans ?? '',
    siddet: m.siddet ?? '',
    ilaveOnlem: m.ilaveOnlem ?? '',
    hedefOlasilik: m.hedefOlasilik ?? '',
    hedefFrekans: m.hedefFrekans ?? '',
    hedefSiddet: m.hedefSiddet ?? '',
    locationNodeId: m.locationNodeId,
    equipmentId: m.equipmentId,
  };
}

/** R11'in kirlilik ölçüsü — alan alan, anahtar sırasından bağımsız. */
function taslakEsitMi(a: MaddeTaslagi, b: MaddeTaslagi): boolean {
  return (Object.keys(a) as (keyof MaddeTaslagi)[]).every((k) => a[k] === b[k]);
}

const EKSEN_KISA: Record<Eksen, string> = { olasilik: 'O', frekans: 'F', siddet: 'Ş' };
const EKSEN_ADI: Record<Eksen, string> = {
  olasilik: 'olasılık',
  frekans: 'frekans',
  siddet: 'şiddet',
};

/** 5x5'te FREKANS EKSENİ HİÇ YOKTUR (§16.12/5) — kolon da çizilmez. */
function eksenler(fineKinney: boolean): Eksen[] {
  return fineKinney ? ['olasilik', 'frekans', 'siddet'] : ['olasilik', 'siddet'];
}

/** Veri kolonu sayısı (eylem kolonu hariç) — soluk satır / pasif satır colSpan'ı. */
function maddeKolonSayisi(fineKinney: boolean): number {
  const skorGrubu = eksenler(fineKinney).length + 2;
  return 6 + skorGrubu + 1 + skorGrubu + 3;
}

interface EkipTaslagi {
  personId: string | null;
  gorevMetni: string;
}
const BOS_EKIP: EkipTaslagi = { personId: null, gorevMetni: '' };

export function RiskDetayScreen(): React.JSX.Element {
  const { assessmentId } = useParams();
  // Yeni kayıt kaydedilince adres değişir; sayfa KENDİ kaydıyla yeniden kurulur
  // (yeni moddaki taslak durum mevcut kaydın detayına sızmasın).
  return (
    <RiskDegerlendirmeSayfasi key={assessmentId ?? 'yeni'} assessmentId={assessmentId ?? null} />
  );
}

function RiskDegerlendirmeSayfasi({
  assessmentId,
}: {
  assessmentId: string | null;
}): React.JSX.Element {
  const navigate = useNavigate();
  const yeniMod = assessmentId === null;
  // R7 — madde listesi SUNUCUDA sayfalanır/süzülür.
  const [maddeSuzgeci, setMaddeSuzgeci] = useState<RiskMaddeSuzgeciUi>({});
  const [maddeArama, setMaddeArama] = useState('');
  const detay = useRiskDegerlendirmesi(assessmentId, maddeSuzgeci);
  const options = useRiskOptions();
  const indir = useCiktiIndir();
  const maddeKaydet = useRiskMaddesiKaydet();
  const maddeSil = useRiskMaddesiSil();
  const uyeEkle = useRiskEkipUyesiEkle();
  const uyeCikar = useRiskEkipUyesiCikar();
  const dondur = useRiskSurumDondur();
  const degerlendirmeSil = useRiskDegerlendirmeSil();

  // Yeni modda yöntem künyede seçilir; tablo başlığı SEÇİLENE göre çizilir.
  const [yeniMetodoloji, setYeniMetodoloji] = useState<RiskMetodolojisi | null>(null);

  const [yeniMadde, setYeniMadde] = useState<MaddeTaslagi>(BOS_MADDE);
  /**
   * ✏️ R11 REVİZYONU (yüzey dürüstlüğü turu — Kapsam §16.12/16-g · §20.3b
   * P1-c): düzenlemeye açılmış HER satır KENDİ taslağını taşır. Başka satıra,
   * sayfaya ya da süzgece geçmek SORU SORMAZ ve taslağı ATMAZ — kaydedilmemiş
   * içerik yerinde SOLUK kalır. `asil` kirlilik ölçüsünün tabanıdır.
   * ⚠️ Eski tek-satır modeline (duzenlenenId + onay modalı) GERİ DÖNME.
   */
  const [taslaklar, setTaslaklar] = useState<
    Record<string, { taslak: MaddeTaslagi; asil: MaddeTaslagi }>
  >({});
  const [etkinId, setEtkinId] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [dondurmaNotu, setDondurmaNotu] = useState<string | null>(null);
  const [yeniUye, setYeniUye] = useState<EkipTaslagi>(BOS_EKIP);
  const [dofMaddesi, setDofMaddesi] = useState<RiskMaddesi | null>(null);

  const veri = detay.data ?? null;
  const metodoloji: RiskMetodolojisi =
    veri?.metodoloji ?? yeniMetodoloji ?? options.data?.metodoloji ?? 'matrix_5x5';
  const fineKinney = metodoloji === 'fine_kinney';
  const kolonSayisi = maddeKolonSayisi(fineKinney);
  // ⚠️ ÖLÇEK KAYDIN KENDİ YÖNTEMİNDEN (tur 11 / R1) — tenant ayarından değil.
  const olcekler = veri?.olcekler;
  // ✏️ KA-2 (bulgu 4): değer + bant GİRİŞTE türetilir — sunucunun yüklemi
  // (`riskSkoruTuret`) ve DEĞERLENDİRMENİN KENDİ YÖNTEMİNİN bant tablosu
  // (tenant varsayılanı değil; yöntem kayıtta donar). İkinci hesap yolu yok.
  const bantTablosu = useRiskBantTablosu(metodoloji, { enabled: veri !== null });
  const bantlar = bantTablosu.data?.bantlar ?? null;
  const duzenleyebilir = veri?.yetkiler.duzenleyebilir ?? false;
  // ✏️ P13 · B1/6 — "Aksiyon aç" `POST actions`ın adımlarından (şapka listesi →
  // bahçe → arşiv); eskiden madde düzenleme bayrağından okunuyordu ve bahçesi
  // dışındaki kullanıcıya düğme görünüp 403 veriyordu.
  const aksiyonAcabilir = veri?.yetkiler.aksiyonKapisi.yazabilir ?? false;

  // ✏️ R11 revizyonu — kirlilik SATIR BAŞINA ölçülür; geçişlerde soru YOK.
  const kirliTaslakIdleri = Object.entries(taslaklar)
    .filter(([, t]) => !taslakEsitMi(t.taslak, t.asil))
    .map(([id]) => id);
  const yeniMaddeKirli = !taslakEsitMi(yeniMadde, BOS_MADDE);
  // Madde 221 — sayfadan/modülden çıkış YÖNLENDİRİCİDE uyarılır (tek nokta;
  // sekme kapatma dahil). Ekran yalnız kaç kaydedilmemiş satırı olduğunu söyler.
  const { serbestGecis } = useKaydedilmemisIcerik(
    kirliTaslakIdleri.length + (yeniMaddeKirli ? 1 : 0),
    'madde satırı',
  );
  // Başka sayfada kalan taslak GÖRÜNMEZ ama kaybolmaz — sayısı süzgecin yanında
  // söylenir (kullanıcı "yazdıklarım nerede" diye aramasın).
  const sayfadakiIdler = new Set((veri?.maddeler ?? []).map((m) => m.id));
  const gorunmeyenKirliSayisi = kirliTaslakIdleri.filter((id) => !sayfadakiIdler.has(id)).length;

  /** Satırı düzenlemeye açar: temiz açık satırlar kapanır, KİRLİLER YERİNDE KALIR. */
  const duzenlemeyeAc = (m: RiskMaddesi): void => {
    setHata(null);
    setTaslaklar((onceki) => {
      const yeni = Object.fromEntries(
        Object.entries(onceki).filter(([id, t]) => id === m.id || !taslakEsitMi(t.taslak, t.asil)),
      );
      if (!yeni[m.id]) {
        const taslak = maddedenTaslak(m);
        yeni[m.id] = { taslak, asil: taslak };
      }
      return yeni;
    });
    setEtkinId(m.id);
  };

  /** P1-c — Vazgeç (düzenlemede): değişikliği at, satırı kapat. */
  const taslagiAt = (id: string): void => {
    setTaslaklar((onceki) => Object.fromEntries(Object.entries(onceki).filter(([k]) => k !== id)));
    setEtkinId((etkin) => (etkin === id ? null : etkin));
  };

  const taslakGuncelleyici =
    (id: string) =>
    (f: (onceki: MaddeTaslagi) => MaddeTaslagi): void =>
      setTaslaklar((onceki) => {
        const mevcut = onceki[id];
        return mevcut ? { ...onceki, [id]: { ...mevcut, taslak: f(mevcut.taslak) } } : onceki;
      });

  const isyeriLokasyonlari = useMemo(
    () =>
      (options.data?.workplaces.find((w) => w.id === veri?.workplaceId)?.locations ?? []).map(
        (l) => ({ value: l.id, label: l.ad }),
      ),
    [options.data, veri?.workplaceId],
  );
  const isyeriEkipmanlari = useMemo(
    () =>
      (options.data?.equipments ?? [])
        .filter((e) => e.workplaceId === veri?.workplaceId)
        .map((e) => ({ value: e.id, label: e.ad })),
    [options.data, veri?.workplaceId],
  );
  const tehlikeler = options.data?.tehlikeler ?? [];
  const riskler = options.data?.riskler ?? [];
  const digerMi = (liste: { id: string; ad: string }[], id: string | null): boolean =>
    id !== null && liste.find((k) => k.id === id)?.ad === 'Diğer';

  // ✏️ §16.12/16-j — KISMİ MADDE: kimlik faaliyet + tehlikedir; risk, önlemler
  // ve skorlar boş kalabilir ("tamamlanmamış" damgası sunucudan türer, sürüm
  // dondurmada kapıdır — çalışma sırasında yol verilir, imzada sıkı olunur).
  const maddeKaydedilebilir = (t: MaddeTaslagi): boolean =>
    t.faaliyet.trim() !== '' && t.tehlikeRefId !== null;

  const sayiVeyaNull = (v: number | ''): number | null => (v === '' ? null : v);

  const maddeGonder = async (itemId: string | null, t: MaddeTaslagi): Promise<boolean> => {
    if (!assessmentId || !maddeKaydedilebilir(t)) return false;
    setHata(null);
    try {
      await maddeKaydet.mutateAsync({
        id: assessmentId,
        itemId: itemId ?? undefined,
        govde: {
          faaliyet: t.faaliyet,
          tehlikeRefId: t.tehlikeRefId,
          tehlikeDigerMetin: t.tehlikeDigerMetin || null,
          riskRefId: t.riskRefId,
          // Risk seçilmemişse "Diğer" metni de gönderilmez (sunucu 422 verirdi).
          riskDigerMetin: t.riskRefId ? t.riskDigerMetin || null : null,
          rutin: t.rutin === null ? null : t.rutin === 'rutin',
          mevcutOnlem: t.mevcutOnlem || null,
          ilgiliMevzuat: t.ilgiliMevzuat || null,
          ilaveOnlem: t.ilaveOnlem || null,
          olasilik: sayiVeyaNull(t.olasilik),
          // 5x5'te frekans GÖNDERİLMEZ (sunucu da reddeder — tek kural, iki
          // yerde görünür). Hedef skorda da aynısı.
          frekans: fineKinney ? sayiVeyaNull(t.frekans) : null,
          siddet: sayiVeyaNull(t.siddet),
          hedefOlasilik: sayiVeyaNull(t.hedefOlasilik),
          hedefFrekans: fineKinney ? sayiVeyaNull(t.hedefFrekans) : null,
          hedefSiddet: sayiVeyaNull(t.hedefSiddet),
          locationNodeId: t.locationNodeId,
          equipmentId: t.equipmentId,
        },
      });
      // ⚠️ KAYIT SÜRERKEN YAZILAN KAYBOLMAZ (İTA dersi — §2.40): satır yalnız
      // GÖNDERİLEN taslağı hâlâ taşıyorsa boşalır/kapanır; sonradan yazılan
      // varsa taslak yerinde kalır ve tabanı kaydedilen hâl olur.
      if (itemId === null) {
        setYeniMadde((simdiki) => (taslakEsitMi(simdiki, t) ? BOS_MADDE : simdiki));
      } else {
        setTaslaklar((onceki) => {
          const mevcut = onceki[itemId];
          if (!mevcut) return onceki;
          if (taslakEsitMi(mevcut.taslak, t)) {
            return Object.fromEntries(Object.entries(onceki).filter(([k]) => k !== itemId));
          }
          return { ...onceki, [itemId]: { taslak: mevcut.taslak, asil: t } };
        });
      }
      return true;
    } catch (error) {
      setHata(error instanceof Error ? error.message : 'Madde kaydedilemedi');
      return false;
    }
  };

  const dondurmaYap = async (): Promise<void> => {
    if (!assessmentId) return;
    setHata(null);
    try {
      const sonuc = await dondur.mutateAsync({ id: assessmentId });
      setDondurmaNotu(
        sonuc.uyarilar.length > 0
          ? `Sürüm ${sonuc.surumNo} donduruldu — ${sonuc.uyarilar.join(' · ')}`
          : `Sürüm ${sonuc.surumNo} donduruldu`,
      );
    } catch (error) {
      setHata(error instanceof Error ? error.message : 'Sürüm dondurulamadı');
    }
  };

  const degerlendirmeyiSil = async (): Promise<void> => {
    if (!assessmentId) return;
    setHata(null);
    try {
      await degerlendirmeSil.mutateAsync({ id: assessmentId });
      // Kayıt silindi: taslakların korunacak bir evi kalmadı — kendi geçişimiz sorulmaz.
      serbestGecis('/risk');
      void navigate('/risk');
    } catch (error) {
      setHata(error instanceof Error ? error.message : 'Değerlendirme silinemedi');
    }
  };

  const olcekEtiketi = (eksen: Eksen, deger: number | null): string | undefined =>
    deger === null
      ? undefined
      : olcekler?.[eksen]?.find((k) => Math.abs(k.deger - deger) < 0.001)?.etiket;

  /** Satırın skor grubu (okuma): eksen değerleri · değer · bant — üç ayrı hücre türü (R10). */
  const skorHucreleri = (
    tur: 'mevcut' | 'hedef',
    girdiler: Record<Eksen, number | null>,
    deger: number | null,
    seviye: RiskSeviyesi | null,
  ): React.ReactNode => (
    <>
      {/* ✏️ B1 — skor kolonları tek haneli değer taşır: SABİT DAR genişlik. */}
      {eksenler(fineKinney).map((eksen) => (
        <Table.Td
          key={`${tur}-${eksen}`}
          className="isg-satir-skor"
          title={olcekEtiketi(eksen, girdiler[eksen])}
          data-testid={`madde-${tur}-${eksen}`}
        >
          {girdiler[eksen] ?? '—'}
        </Table.Td>
      ))}
      <Table.Td className="isg-satir-skor" data-testid={`madde-${tur}-deger`}>
        {deger ?? '—'}
      </Table.Td>
      <Table.Td className="isg-satir-skor">
        <SeviyeRozeti
          seviye={seviye}
          hamSkor={deger}
          testOn={tur === 'mevcut' ? 'risk-seviye' : 'risk-hedef-seviye'}
        />
      </Table.Td>
    </>
  );

  /**
   * P1-b — ALANLARIN SATIRA SIĞMASI (R8: tek satır). Aynı hücreler hem soluk
   * satırda (yeni) hem düzenlenen satırda (mevcut) çizilir — iki form
   * yazılmaz. ✏️ KA-2: değer/bant hücreleri girişte TÜRETİLİR (`riskSkoruTuret`
   * — sunucuyla aynı yüklem, kaydın yönteminin bant tablosu).
   */
  const maddeHucreleri = (
    t: MaddeTaslagi,
    setT: (f: (onceki: MaddeTaslagi) => MaddeTaslagi) => void,
    testOn: string,
    mevcut: RiskMaddesi | null,
  ): { hucreler: React.ReactNode; ikinciKatman: React.ReactNode } => {
    const metinAlani = (
      alan: 'faaliyet' | 'mevcutOnlem' | 'ilgiliMevzuat' | 'ilaveOnlem',
      etiket: string,
      testSonek: string,
    ): React.ReactNode => (
      // ✏️ GEZİNTİ ARTÇISI 3 / B1: metin hücresi YANA değil AŞAĞI büyür
      // (Excel deseni) — sabit genişlik `.isg-satir-metin`den, satır sayısı
      // içerikten. TextInput tek satırdı ve uzun metni kırpıyordu.
      <Textarea
        size="xs"
        autosize
        minRows={1}
        className="isg-satir-metin"
        placeholder={etiket}
        aria-label={etiket}
        value={t[alan]}
        onChange={(e) => {
          const deger = e.currentTarget.value;
          setT((o) => ({ ...o, [alan]: deger }));
        }}
        data-testid={`${testOn}-${testSonek}`}
      />
    );
    const olcekGrubu = (tur: 'mevcut' | 'hedef'): React.ReactNode => (
      <>
        {eksenler(fineKinney).map((eksen) => {
          const alan: keyof MaddeTaslagi =
            tur === 'mevcut'
              ? eksen
              : eksen === 'olasilik'
                ? 'hedefOlasilik'
                : eksen === 'frekans'
                  ? 'hedefFrekans'
                  : 'hedefSiddet';
          return (
            <Table.Td key={`${tur}-${eksen}`}>
              <OlcekSecimi
                etiket={`${tur === 'mevcut' ? 'Mevcut' : 'Hedef'} ${EKSEN_ADI[eksen]}`}
                kisaEtiket={EKSEN_KISA[eksen]}
                kademeler={olcekler?.[eksen] ?? undefined}
                deger={t[alan] as number | ''}
                secildi={(v) => setT((o) => ({ ...o, [alan]: v }))}
                testId={tur === 'mevcut' ? `${testOn}-${eksen}` : `${testOn}-hedef-${eksen}`}
              />
            </Table.Td>
          );
        })}
        {(() => {
          const sayi = (v: number | ''): number | null => (v === '' ? null : v);
          const girdi =
            tur === 'mevcut'
              ? { olasilik: sayi(t.olasilik), frekans: sayi(t.frekans), siddet: sayi(t.siddet) }
              : {
                  olasilik: sayi(t.hedefOlasilik),
                  frekans: sayi(t.hedefFrekans),
                  siddet: sayi(t.hedefSiddet),
                };
          // Bant tablosu gelmeden bant "yok" denmez — tablo yüklenene dek boş.
          const skor = bantlar === null ? null : riskSkoruTuret(metodoloji, girdi, bantlar);
          return (
            <>
              <Table.Td className="isg-satir-skor" data-testid={`${testOn}-${tur}-deger`}>
                <Text size="xs">{skor?.hamSkor ?? '—'}</Text>
              </Table.Td>
              <Table.Td className="isg-satir-skor">
                <SeviyeRozeti
                  seviye={skor?.riskSeviyesi ?? null}
                  hamSkor={skor?.hamSkor ?? null}
                  testOn={`${testOn}-${tur}-seviye`}
                />
              </Table.Td>
            </>
          );
        })()}
      </>
    );
    return {
      hucreler: (
        <>
          <Table.Td className="isg-sabit-kolon isg-sabit-kolon-1">
            {metinAlani('faaliyet', 'Faaliyet', 'faaliyet')}
          </Table.Td>
          <Table.Td className="isg-sabit-kolon isg-sabit-kolon-2">
            <Select
              size="xs"
              placeholder="Tehlike (kaynak)"
              aria-label="Tehlike (kaynak)"
              data={tehlikeler.map((k) => ({ value: k.id, label: k.ad }))}
              value={t.tehlikeRefId}
              onChange={(v) => setT((o) => ({ ...o, tehlikeRefId: v }))}
              searchable
              data-testid={`${testOn}-tehlike`}
            />
          </Table.Td>
          <Table.Td>
            <Select
              size="xs"
              className="isg-satir-metin"
              placeholder="Risk (gerçekleşme)"
              aria-label="Risk (gerçekleşme)"
              data={riskler.map((k) => ({ value: k.id, label: k.ad }))}
              value={t.riskRefId}
              onChange={(v) => setT((o) => ({ ...o, riskRefId: v }))}
              // §16.12/16-j — risk opsiyonel: seçim geri alınabilir.
              clearable
              searchable
              data-testid={`${testOn}-risk`}
            />
          </Table.Td>
          <Table.Td>
            <Select
              size="xs"
              className="isg-satir-olcek"
              placeholder="Rutin?"
              aria-label="Rutin / rutin değil"
              data={[
                { value: 'rutin', label: 'Rutin' },
                { value: 'rutin_degil', label: 'Rutin değil' },
              ]}
              value={t.rutin}
              onChange={(v) =>
                setT((o) => ({
                  ...o,
                  rutin: v === 'rutin' || v === 'rutin_degil' ? v : null,
                }))
              }
              clearable
              data-testid={`${testOn}-rutin`}
            />
          </Table.Td>
          <Table.Td className="isg-satir-metin">
            {metinAlani('mevcutOnlem', 'Mevcut önlem', 'mevcut-onlem')}
          </Table.Td>
          <Table.Td className="isg-satir-metin">
            {metinAlani('ilgiliMevzuat', 'İlgili mevzuat', 'ilgili-mevzuat')}
          </Table.Td>
          {olcekGrubu('mevcut')}
          <Table.Td className="isg-satir-metin">
            {metinAlani('ilaveOnlem', 'Planlanan önlem', 'ilave-onlem')}
          </Table.Td>
          {olcekGrubu('hedef')}
          {/* R7 kararı: sorumlu + termin AKSİYONDAN — girişte salt okunur. */}
          <TakipHucreleri madde={mevcut} testOn={testOn} />
        </>
      ),
      ikinciKatman: (
        <Group gap="xs" align="flex-end" wrap="wrap">
          {digerMi(tehlikeler, t.tehlikeRefId) && (
            <TextInput
              size="xs"
              label="Tehlike (Diğer)"
              value={t.tehlikeDigerMetin}
              onChange={(e) => {
                const deger = e.currentTarget.value;
                setT((o) => ({ ...o, tehlikeDigerMetin: deger }));
              }}
              data-testid={`${testOn}-tehlike-diger`}
            />
          )}
          {digerMi(riskler, t.riskRefId) && (
            <TextInput
              size="xs"
              label="Risk (Diğer)"
              value={t.riskDigerMetin}
              onChange={(e) => {
                const deger = e.currentTarget.value;
                setT((o) => ({ ...o, riskDigerMetin: deger }));
              }}
              data-testid={`${testOn}-risk-diger`}
            />
          )}
          {/* İKİ HEDEF SORGUNUN TAŞIYICILARI — serbest metinle kurulamaz. */}
          <Select
            size="xs"
            label="Lokasyon bağı"
            data={isyeriLokasyonlari}
            value={t.locationNodeId}
            onChange={(v) => setT((o) => ({ ...o, locationNodeId: v }))}
            clearable
            searchable
            data-testid={`${testOn}-lokasyon`}
          />
          <Select
            size="xs"
            label="Ekipman bağı"
            data={isyeriEkipmanlari}
            value={t.equipmentId}
            onChange={(v) => setT((o) => ({ ...o, equipmentId: v }))}
            clearable
            searchable
            data-testid={`${testOn}-ekipman`}
          />
        </Group>
      ),
    };
  };

  const solukSatiri = (): React.ReactNode => {
    const { hucreler, ikinciKatman } = maddeHucreleri(yeniMadde, setYeniMadde, 'soluk', null);
    return (
      <SolukSatir
        key="soluk"
        dokunuldu={!taslakEsitMi(yeniMadde, BOS_MADDE)}
        kaydedilebilir={maddeKaydedilebilir(yeniMadde)}
        // C1 — "kaydediliyor" YALNIZ bu satırın isteğidir; başka satırın kaydı
        // bu satırı "kaydediliyor" göstermez (iki hâl yüzeyde ayrı).
        kaydediliyor={maddeKaydet.isPending && maddeKaydet.variables?.itemId === undefined}
        taslakDurumu={null}
        onKaydet={() => void maddeGonder(null, yeniMadde)}
        // P1-c: soluk satırda Vazgeç = "satırı boşalt".
        onVazgec={() => setYeniMadde(BOS_MADDE)}
        kolonSayisi={kolonSayisi}
        ikinciKatman={ikinciKatman}
        testId="madde-soluk-satir"
      >
        {hucreler}
      </SolukSatir>
    );
  };

  const maddeSatiri = (m: RiskMaddesi): React.ReactNode => {
    const acik = taslaklar[m.id];
    if (acik) {
      const { hucreler, ikinciKatman } = maddeHucreleri(
        acik.taslak,
        taslakGuncelleyici(m.id),
        'duzenle',
        m,
      );
      const etkin = etkinId === m.id;
      const kirli = !taslakEsitMi(acik.taslak, acik.asil);
      return (
        <SolukSatir
          key={m.id}
          // ✏️ R11 revizyonu: ETKİN satır tam opak; bırakılan kirli satır
          // YERİNDE SOLUK kalır ve "kaydedilmedi" der — soru sorulmaz.
          dokunuldu={etkin}
          taslakDurumu={kirli ? 'kaydedilmedi' : null}
          kayitKimligi={m.id}
          onEtkinlestir={() => setEtkinId(m.id)}
          kaydedilebilir={maddeKaydedilebilir(acik.taslak)}
          kaydediliyor={maddeKaydet.isPending && maddeKaydet.variables?.itemId === m.id}
          onKaydet={() => void maddeGonder(m.id, acik.taslak)}
          // P1-c: düzenlemede Vazgeç = "değişikliği at".
          onVazgec={() => taslagiAt(m.id)}
          kolonSayisi={kolonSayisi}
          testId="madde-duzenleme-satiri"
          ikinciKatman={ikinciKatman}
        >
          {hucreler}
        </SolukSatir>
      );
    }
    return (
      <Table.Tr key={m.id} data-testid="madde-satiri">
        <Table.Td className="isg-sabit-kolon isg-sabit-kolon-1">
          {m.faaliyet}
          {(m.locationAd || m.equipmentAd) && (
            <Text size="xs" c="dimmed">
              {[m.locationAd, m.equipmentAd].filter(Boolean).join(' · ')}
            </Text>
          )}
          {/* ✏️ §16.12/16-j — TAMAMLANMAMIŞ DAMGASI sunucudan (türetilir, kolon
              yok). Sabit kolonda durur: yatay kaydırmada da görünür kalır. */}
          {m.eksikler.length > 0 && (
            <Group gap={4} mt={2}>
              {m.eksikler.map((eksik) => (
                <Rozet key={eksik} anlam="dikkat" testId={`madde-eksik-${eksik}`}>
                  {RISK_MADDE_EKSIK_ETIKETI[eksik]}
                </Rozet>
              ))}
            </Group>
          )}
        </Table.Td>
        <Table.Td className="isg-sabit-kolon isg-sabit-kolon-2">
          {m.tehlike}
          {m.tehlikeDigerMetin ? ` (${m.tehlikeDigerMetin})` : ''}
        </Table.Td>
        <Table.Td data-testid="madde-risk">
          {m.risk ?? '—'}
          {m.risk && m.riskDigerMetin ? ` (${m.riskDigerMetin})` : ''}
        </Table.Td>
        <Table.Td data-testid="madde-rutin">{riskRutinEtiketi(m.rutin)}</Table.Td>
        {/* ✏️ B1 — metin hücreleri SABİT genişlik + SARMA (`.isg-satir-metin`):
            içerik uzadıkça tablo yana şişmez, satır yükselir. */}
        <Table.Td className="isg-satir-metin" data-testid="madde-mevcut-onlem">
          {m.mevcutOnlem ?? '—'}
        </Table.Td>
        <Table.Td className="isg-satir-metin" data-testid="madde-ilgili-mevzuat">
          {m.ilgiliMevzuat ?? '—'}
        </Table.Td>
        {skorHucreleri(
          'mevcut',
          { olasilik: m.olasilik, frekans: m.frekans, siddet: m.siddet },
          m.hamSkor,
          m.riskSeviyesi,
        )}
        <Table.Td className="isg-satir-metin" data-testid="madde-planlanan-onlem">
          {m.ilaveOnlem ?? '—'}
        </Table.Td>
        {skorHucreleri(
          'hedef',
          { olasilik: m.hedefOlasilik, frekans: m.hedefFrekans, siddet: m.hedefSiddet },
          m.hedefHamSkor,
          m.hedefRiskSeviyesi,
        )}
        <TakipHucreleri madde={m} testOn="madde" />
        <Table.Td>
          <SatirEylemleri
            testId="madde-eylemleri"
            birincil={
              aksiyonAcabilir
                ? {
                    // Risk maddesinden DÖF: YENİ UÇ YOK — mevcut aksiyon
                    // formu `riskItemId` ile açılır (hedefe bağlanır, R5).
                    etiket: 'Aksiyon aç',
                    onClick: () => setDofMaddesi(m),
                    testId: 'madde-aksiyon-ac',
                  }
                : undefined
            }
            digerleri={[
              {
                etiket: 'Düzenle',
                gizli: !duzenleyebilir,
                // ✏️ R11 revizyonu: SORU YOK — başka satırdaki kirli taslak YERİNDE kalır.
                onClick: () => duzenlemeyeAc(m),
                testId: 'madde-duzenle',
              },
              {
                etiket: 'Sil',
                tehlikeli: true,
                gizli: !duzenleyebilir,
                onClick: () => {
                  if (!assessmentId) return;
                  setHata(null);
                  maddeSil
                    .mutateAsync({ id: assessmentId, itemId: m.id })
                    // Silinen maddenin taslağı kalmaz (kaydedilmemiş sayısını şişirmesin).
                    .then(() => taslagiAt(m.id))
                    .catch((error: unknown) =>
                      setHata(error instanceof Error ? error.message : 'Madde silinemedi'),
                    );
                },
                testId: 'madde-sil',
              },
            ]}
          />
        </Table.Td>
      </Table.Tr>
    );
  };

  const tabloBasligi = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th rowSpan={2} className="isg-sabit-kolon isg-sabit-kolon-1">
          Faaliyet
        </Table.Th>
        <Table.Th rowSpan={2} className="isg-sabit-kolon isg-sabit-kolon-2">
          Tehlike (kaynak)
        </Table.Th>
        <Table.Th rowSpan={2}>Risk (gerçekleşme)</Table.Th>
        <Table.Th rowSpan={2}>Rutin</Table.Th>
        <Table.Th rowSpan={2} className="isg-satir-metin">
          Mevcut önlem
        </Table.Th>
        <Table.Th rowSpan={2} className="isg-satir-metin">
          İlgili mevzuat
        </Table.Th>
        <Table.Th colSpan={eksenler(fineKinney).length + 2} data-testid="madde-baslik-mevcut-skor">
          Mevcut skor
        </Table.Th>
        <Table.Th rowSpan={2} className="isg-satir-metin">
          Planlanan önlem
        </Table.Th>
        <Table.Th colSpan={eksenler(fineKinney).length + 2} data-testid="madde-baslik-hedef-skor">
          Hedef skor
        </Table.Th>
        <Table.Th rowSpan={2}>Sorumlu</Table.Th>
        <Table.Th rowSpan={2}>Termin</Table.Th>
        <Table.Th rowSpan={2}>Aksiyon</Table.Th>
        <Table.Th rowSpan={2} />
      </Table.Tr>
      <Table.Tr>
        {(['mevcut', 'hedef'] as const).flatMap((tur) => [
          ...eksenler(fineKinney).map((eksen) => (
            <Table.Th
              key={`${tur}-${eksen}`}
              className="isg-satir-skor"
              data-testid={`madde-baslik-${tur}-${eksen}`}
            >
              {EKSEN_KISA[eksen]}
            </Table.Th>
          )),
          <Table.Th key={`${tur}-deger`} className="isg-satir-skor">
            Değer
          </Table.Th>,
          <Table.Th key={`${tur}-bant`} className="isg-satir-skor">
            Bant
          </Table.Th>,
        ])}
      </Table.Tr>
    </Table.Thead>
  );

  // ✏️ R11 revizyonu: süzgeç/sayfa değişimi SORU SORMAZ ve taslakları ATMAZ —
  // görünmeyen sayfaya düşen taslak kaybolmaz, sayısı süzgecin yanında görünür.
  const maddeSuzgeciDegistir = (yeni: RiskMaddeSuzgeciUi): void => setMaddeSuzgeci(yeni);

  if (!yeniMod && !veri) {
    return (
      <Stack>
        <QueryStatus queries={[detay, options]} kayit />
      </Stack>
    );
  }

  return (
    <Stack>
      <QueryStatus queries={yeniMod ? [options] : [detay, options]} kayit />
      <KayitDetayi
        geriYol="/risk"
        geriEtiket="Risk değerlendirmeleri"
        baslik={veri?.ad ?? 'Yeni risk değerlendirmesi'}
        testId="risk-detay"
        ustBilgi={
          veri && (
            <Group gap={8}>
              <Text size="sm" c="dimmed">
                {veri.workplaceAd}
              </Text>
              <KapsamMetni
                kapsamTipi={veri.kapsamTipi}
                locationAd={veri.locationAd}
                faaliyetAdi={veri.faaliyetAdi}
              />
              <Rozet anlam="bilgi">{RISK_METODOLOJI_ETIKETI[veri.metodoloji]}</Rozet>
              {veri.imzadanBeriDegisti && (
                <Rozet anlam="dikkat" testId="detay-imzadan-beri-degisti">
                  imzadan beri değişti ({veri.degisiklik.eklenen}+ / {veri.degisiklik.degisen}~ /{' '}
                  {veri.degisiklik.silinen}-)
                </Rozet>
              )}
              {veri.vadesiDoldu && <Rozet anlam="gecikmis">yenileme vadesi doldu</Rozet>}
            </Group>
          )
        }
        eylemler={
          veri && (
            <SatirEylemleri
              testId="risk-detay-eylemleri"
              /* ⚠️ BİR BİRİNCİL EYLEM (P2) — kullanıcıya göre: yazabilene
                 "Sürümü dondur", yazamayana (izleyici dahil) PDF. */
              birincil={
                veri.yetkiler.dondurabilir
                  ? {
                      etiket: 'Sürümü dondur',
                      onClick: () => void dondurmaYap(),
                      testId: 'surum-dondur',
                    }
                  : {
                      etiket: 'PDF indir',
                      onClick: () =>
                        indir.mutate({
                          yol: `/api/risk-assessments/${veri.id}/pdf`,
                          yedekAd: 'risk-degerlendirmesi.pdf',
                        }),
                      testId: 'risk-pdf-canli',
                    }
              }
              digerleri={[
                {
                  etiket: 'PDF indir (canlı)',
                  gizli: !veri.yetkiler.dondurabilir,
                  pasif: indir.isPending,
                  onClick: () =>
                    indir.mutate({
                      yol: `/api/risk-assessments/${veri.id}/pdf`,
                      yedekAd: 'risk-degerlendirmesi.pdf',
                    }),
                  testId: 'risk-pdf-canli-menu',
                },
                {
                  etiket: 'Değerlendirmeyi sil',
                  tehlikeli: true,
                  gizli: !duzenleyebilir,
                  pasif: degerlendirmeSil.isPending,
                  onClick: () => void degerlendirmeyiSil(),
                  testId: 'risk-degerlendirme-sil',
                },
              ]}
            />
          )
        }
      >
        {veri && veri.uyarilar.length > 0 && (
          <Alert color={ANLAM.dikkat} data-testid="risk-uyarilar">
            <Stack gap={2}>
              {veri.uyarilar.map((u) => (
                <Text key={u} size="sm">
                  {u}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        {dondurmaNotu && (
          <Alert color={ANLAM.olumlu} data-testid="dondurma-notu">
            {dondurmaNotu}
          </Alert>
        )}
        {hata && (
          <Alert color={ANLAM.engel} data-testid="risk-detay-hata">
            {hata}
          </Alert>
        )}

        {options.data && (
          <KunyeBolumu
            veri={veri}
            options={options.data}
            metodoloji={metodoloji}
            setMetodoloji={setYeniMetodoloji}
            onOlusturuldu={(id) => void navigate(`/risk/${id}`, { replace: true })}
          />
        )}

        <Bolum baslik={veri ? `Maddeler (${veri.maddeSayisi})` : 'Maddeler'} testId="madde-bolumu">
          {veri && (
            /* R7 — ARAMA + SÜZGEÇ SUNUCUDA. Süzgeç MADDEYİ süzer, ÖZETİ
               süzmez. R11: süzgeç/sayfa değişimi kirli düzenlemeyi SORAR. */
            <Group align="flex-end" mb="xs">
              <TextInput
                label="Maddelerde ara"
                placeholder="Faaliyet · tehlike · risk · önlem"
                value={maddeArama}
                onChange={(e) => setMaddeArama(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    maddeSuzgeciDegistir({ ...maddeSuzgeci, q: maddeArama, sayfa: 1 });
                  }
                }}
                data-testid="madde-arama"
              />
              <Button
                variant="default"
                data-testid="madde-ara"
                onClick={() => maddeSuzgeciDegistir({ ...maddeSuzgeci, q: maddeArama, sayfa: 1 })}
              >
                Ara
              </Button>
              <Select
                label="Bant (mevcut skor)"
                data={[
                  ...RISK_SEVIYELERI.map((seviye) => ({
                    value: seviye,
                    label: RISK_SEVIYESI_ETIKETI[seviye],
                  })),
                  // ⚠️ "Bantsız" ≠ "skorlanmamış" (§16.12/16-j): bant tablosu
                  // boşken skorlu madde de bantsızdır — ayrı süzgeç aşağıda.
                  { value: 'yok', label: 'Bantsız' },
                ]}
                value={maddeSuzgeci.seviye ?? null}
                onChange={(v) => maddeSuzgeciDegistir({ ...maddeSuzgeci, seviye: v, sayfa: 1 })}
                clearable
                data-testid="madde-seviye-suzgeci"
              />
              <Select
                label="Durum"
                data={[{ value: 'eksik', label: 'Tamamlanmamış (skor/risk eksik)' }]}
                value={maddeSuzgeci.durum ?? null}
                onChange={(v) => maddeSuzgeciDegistir({ ...maddeSuzgeci, durum: v, sayfa: 1 })}
                clearable
                data-testid="madde-durum-suzgeci"
              />
              <SuzgecGostergesi
                etkinSayisi={
                  (maddeSuzgeci.q?.trim() ? 1 : 0) +
                  (maddeSuzgeci.seviye ? 1 : 0) +
                  (maddeSuzgeci.durum ? 1 : 0)
                }
                onTemizle={() => {
                  setMaddeArama('');
                  setMaddeSuzgeci({});
                }}
                testId="madde-suzgec-gostergesi"
              />
              {gorunmeyenKirliSayisi > 0 && (
                <Rozet anlam="dikkat" testId="madde-gorunmeyen-taslak">
                  {gorunmeyenKirliSayisi} kaydedilmemiş satır bu sayfada görünmüyor
                </Rozet>
              )}
              {/* ✏️ KA-4 (bulgu 1): R12'nin "Madde ekle" tetikleyicisi KALKTI —
                  soluk satır sonda TEK giriş yoludur (iki yol ikinci kapıdır). */}
            </Group>
          )}
          <Box className="isg-yatay-kaydirma" data-testid="madde-kaydirma">
            <Table data-testid="madde-tablosu">
              {tabloBasligi}
              <Table.Tbody>
                {!veri && (
                  /* R6 — KÜNYE KAYDEDİLMEDEN MADDE SATIRI PASİF, SEBEBİ SATIRDA. */
                  <Table.Tr data-testid="madde-pasif-satir" data-pasif="evet">
                    <Table.Td colSpan={kolonSayisi + 1}>
                      <YazamamaSebebi testId="madde-pasif-sebep">
                        Madde satırı künye kaydedilince açılır — yöntem kayıtta dondurulur ve puan
                        ölçeği ona göre gelir.
                      </YazamamaSebebi>
                    </Table.Td>
                  </Table.Tr>
                )}
                {veri?.maddeler.map(maddeSatiri)}
                {/* P1-a — SOLUK SATIR listenin SONUNDA (KA-4: tek giriş yolu). */}
                {veri && duzenleyebilir && solukSatiri()}
              </Table.Tbody>
            </Table>
          </Box>
          {veri && (
            <KapiSebebi
              durum={veri.yetkiler.yazmaKapisi}
              nesne="Maddeyi"
              fiil="ekler ve düzenler"
              testId="risk-madde-yazamama-sebebi"
            />
          )}
          {/* Madde yazabilen ama aksiyon açamayan kullanıcıya ikinci sebep (yazamayana tek satır). */}
          {veri && veri.yetkiler.yazmaKapisi.yazabilir && (
            <KapiSebebi
              durum={veri.yetkiler.aksiyonKapisi}
              nesne="Maddeden aksiyonu"
              fiil="açar"
              testId="risk-aksiyon-yazamama-sebebi"
            />
          )}
          {veri && (
            <Sayfalama
              sayfa={veri.maddeSayfa}
              sayfaBoyutu={veri.maddeSayfaBoyutu}
              toplam={veri.maddeToplam}
              onDegis={(sayfa) => maddeSuzgeciDegistir({ ...maddeSuzgeci, sayfa })}
              testId="madde-sayfalama"
            />
          )}
        </Bolum>

        {veri && (
          <CokenBolum
            baslik="Değerlendirme ekibi"
            sayi={veri.ekip.length}
            testId="ekip-bolumu"
            ekleyebilir={duzenleyebilir}
          >
            <Bolum baslik="Değerlendirme ekibi" testId="ekip-bolumu">
              {/* R6: ÜYELİK YETKİ VERMEZ — katılım kaydıdır. Ekip üyesi ALT
                  KALEMDİR: soluk satır sonda, "Çıkar" ⋮ altında (P2-a). */}
              <Table data-testid="ekip-tablosu">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Kişi</Table.Th>
                    <Table.Th>Görev</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {veri.ekip.map((u) => (
                    <Table.Tr key={u.id} data-testid="ekip-uyesi">
                      <Table.Td>{u.adSoyad}</Table.Td>
                      <Table.Td>{u.gorevMetni ?? '—'}</Table.Td>
                      <Table.Td>
                        <SatirEylemleri
                          testId="ekip-uye-eylemleri"
                          digerleri={[
                            {
                              etiket: 'Ekipten çıkar',
                              tehlikeli: true,
                              gizli: !duzenleyebilir,
                              onClick: () =>
                                void uyeCikar.mutateAsync({ id: veri.id, memberId: u.id }),
                              testId: 'ekip-uye-cikar',
                            },
                          ]}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {duzenleyebilir && (
                    <SolukSatir
                      dokunuldu={yeniUye !== BOS_EKIP}
                      kaydedilebilir={yeniUye.personId !== null}
                      kaydediliyor={uyeEkle.isPending}
                      kolonSayisi={2}
                      testId="ekip-soluk-satir"
                      onVazgec={() => setYeniUye(BOS_EKIP)}
                      onKaydet={() => {
                        if (!yeniUye.personId) return;
                        uyeEkle.mutate(
                          {
                            id: veri.id,
                            personId: yeniUye.personId,
                            gorevMetni: yeniUye.gorevMetni || null,
                          },
                          { onSuccess: () => setYeniUye(BOS_EKIP) },
                        );
                      }}
                    >
                      <Table.Td>
                        <Select
                          size="xs"
                          placeholder="Kişi"
                          data={(options.data?.personeller ?? []).map((p) => ({
                            value: p.id,
                            label: p.adSoyad,
                          }))}
                          value={yeniUye.personId}
                          onChange={(v) => setYeniUye((o) => ({ ...o, personId: v }))}
                          searchable
                          data-testid="ekip-kisi"
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          placeholder="Görev (serbest)"
                          value={yeniUye.gorevMetni}
                          onChange={(e) => {
                            const deger = e.currentTarget.value;
                            setYeniUye((o) => ({ ...o, gorevMetni: deger }));
                          }}
                          data-testid="ekip-gorev"
                        />
                      </Table.Td>
                    </SolukSatir>
                  )}
                </Table.Tbody>
              </Table>
              <KapiSebebi
                durum={veri.yetkiler.yazmaKapisi}
                nesne="Ekip üyesini"
                fiil="ekler"
                testId="risk-ekip-yazamama-sebebi"
              />
            </Bolum>
          </CokenBolum>
        )}

        {/* TOPLU YÜKLEME (F4·S9) — panel yalnız YAZABİLENE görünür. */}
        {veri && duzenleyebilir && (
          <Bolum baslik="Maddeleri toplu yükle" testId="risk-yukleme-bolumu">
            <RiskMaddeYuklemePaneli assessmentId={veri.id} />
          </Bolum>
        )}

        {veri && (
          <CokenBolum
            baslik="Sürüm geçmişi"
            sayi={veri.surumler.length}
            testId="surum-bolumu"
            ekleyebilir={false}
          >
            <Bolum baslik="Sürüm geçmişi" testId="surum-bolumu">
              <Stack gap={2}>
                {veri.surumler.map((s) => (
                  <Group key={s.id} gap="xs" data-testid="surum-satiri">
                    <Text size="sm">
                      v{s.surumNo} · {tarihGoster(s.dondurulduAt)} · {s.maddeSayisi} madde
                      {s.not ? ` — ${s.not}` : ''}
                    </Text>
                    {/* ⚠️ SÜRÜM KİLİDİ: çıktı O SÜRÜMÜN snapshot'ını taşır. */}
                    <Anchor
                      size="sm"
                      data-testid="surum-pdf"
                      onClick={() =>
                        indir.mutate({
                          yol: `/api/risk-assessments/${veri.id}/pdf?surum=${s.surumNo}`,
                          yedekAd: `risk-degerlendirmesi-surum-${s.surumNo}.pdf`,
                        })
                      }
                    >
                      PDF indir
                    </Anchor>
                  </Group>
                ))}
              </Stack>
            </Bolum>
          </CokenBolum>
        )}
      </KayitDetayi>

      {/* ✏️ R11 revizyonu: satırlar arası ONAY MODALI KALKTI (yüzey dürüstlüğü
          turu). Sayfadan/modülden çıkış uyarısı yönlendiricidedir —
          `shell/KaydedilmemisIcerik` (madde 221). */}

      {/* RİSK MADDESİNDEN DÖF (§16.12/1'in üçüncü bağı): MEVCUT aksiyon
          formu + `riskItemId` — YENİ UÇ YOK. Modal burada MEŞRUDUR: aksiyon
          kendi sayfası olan bağımsız kayıttır; bu bir "onay + kimlik" sorusu. */}
      <Modal
        opened={dofMaddesi !== null}
        onClose={() => setDofMaddesi(null)}
        title="Risk maddesinden aksiyon aç"
      >
        {dofMaddesi && veri && (
          <RiskDofFormu
            madde={dofMaddesi}
            workplaceId={veri.workplaceId}
            onClose={() => setDofMaddesi(null)}
          />
        )}
      </Modal>
    </Stack>
  );
}

/**
 * KÜNYE — sayfanın ÜST KISMI (madde 220 / R6). Yeni kayıtta işyeri · ad ·
 * yöntem · kapsam doldurulur ve kaydedilince tablo açılır; mevcut kayıtta
 * aynı yerde düzeltilir.
 *
 * ⚠️ **İŞYERİ ve YÖNTEM mevcut kayıtta SALT OKUNURDUR** (§16.12/2 kilidi):
 * işyeri bahçe eksenidir, yöntem kaydın ölçeğidir — düzeltme ucu ikisine de
 * dokunmaz. ⚠️ **Künye düzeltme ucunun (`PATCH`) web'de 23 gün boyunca hiç
 * çağıranı yoktu** (h-3); çağıran burasıdır.
 *
 * P9 — kısa alanlar ızgarada.
 */
function KunyeBolumu({
  veri,
  options,
  metodoloji,
  setMetodoloji,
  onOlusturuldu,
}: {
  veri: Detay | null;
  options: Options;
  metodoloji: RiskMetodolojisi;
  setMetodoloji: (m: RiskMetodolojisi) => void;
  onOlusturuldu: (id: string) => void;
}): React.JSX.Element {
  const kaydet = useRiskDegerlendirmeKaydet();
  const [workplaceId, setWorkplaceId] = useState<string | null>(veri?.workplaceId ?? null);
  const [ad, setAd] = useState(veri?.ad ?? '');
  const [kapsamTipi, setKapsamTipi] = useState<RiskKapsamTipi>(veri?.kapsamTipi ?? 'faaliyet');
  const [locationNodeId, setLocationNodeId] = useState<string | null>(veri?.locationNodeId ?? null);
  const [faaliyetAdi, setFaaliyetAdi] = useState(veri?.faaliyetAdi ?? '');
  const [hata, setHata] = useState<string | null>(null);

  const yeniMod = veri === null;
  const yazabilir = yeniMod ? options.kayitAcabilir : veri.yetkiler.duzenleyebilir;
  const lokasyonlar = useMemo(() => {
    const liste = (options.workplaces.find((w) => w.id === workplaceId)?.locations ?? []).map(
      (l) => ({ value: l.id, label: l.ad }),
    );
    // Arşive gitmiş işyerinin lokasyonu seçenek havuzunda yoktur; kaydın
    // kendi değeri yine görünür kalır.
    if (veri?.locationNodeId && !liste.some((l) => l.value === veri.locationNodeId)) {
      liste.push({ value: veri.locationNodeId, label: veri.locationAd ?? '—' });
    }
    return liste;
  }, [options.workplaces, workplaceId, veri?.locationNodeId, veri?.locationAd]);

  const kirli =
    yeniMod ||
    ad !== veri.ad ||
    kapsamTipi !== veri.kapsamTipi ||
    (kapsamTipi === 'lokasyon' ? locationNodeId : null) !== veri.locationNodeId ||
    (kapsamTipi === 'faaliyet' ? faaliyetAdi : '') !== (veri.faaliyetAdi ?? '');
  // Madde 221 — künyeyle çıkış YÖNLENDİRİCİDE uyarılır. Yeni modda `kirli` her
  // zaman doğrudur (Kaydet açık kalsın diye); korunacak içerik ise yalnız
  // kullanıcının GİRDİĞİ değerdir — boş sayfadan çıkmak sorulmaz.
  const kaydedilmemis = yeniMod
    ? workplaceId !== null ||
      ad.trim() !== '' ||
      faaliyetAdi.trim() !== '' ||
      locationNodeId !== null
    : yazabilir && kirli;
  const { serbestGecis } = useKaydedilmemisIcerik(kaydedilmemis ? 1 : 0, 'künye');

  const gonder = async (): Promise<void> => {
    setHata(null);
    const kapsam = {
      kapsamTipi,
      // Kapsam tek taşıyıcı gönderir; sunucu ayrıca doğrular (DB CHECK +
      // servis guard'ı).
      locationNodeId: kapsamTipi === 'lokasyon' ? locationNodeId : null,
      faaliyetAdi: kapsamTipi === 'faaliyet' ? faaliyetAdi : null,
    };
    try {
      if (yeniMod) {
        const sonuc = (await kaydet.mutateAsync({
          govde: { workplaceId, ad, metodoloji, ...kapsam },
        })) as { id: string };
        // Kayıt açıldı: kendi adresine geçiş SORULMAZ (hedef adıyla serbest).
        serbestGecis(`/risk/${sonuc.id}`);
        onOlusturuldu(sonuc.id);
      } else {
        await kaydet.mutateAsync({ id: veri.id, govde: { ad, ...kapsam } });
      }
    } catch (error) {
      setHata(error instanceof Error ? error.message : 'Künye kaydedilemedi');
    }
  };

  return (
    <Bolum baslik="Künye" testId="risk-kunye">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        <Select
          label="İşyeri"
          data={
            yeniMod
              ? options.workplaces.map((w) => ({ value: w.id, label: `${w.companyAd} — ${w.ad}` }))
              : [{ value: veri.workplaceId, label: veri.workplaceAd }]
          }
          value={workplaceId}
          onChange={(v) => {
            setWorkplaceId(v);
            setLocationNodeId(null);
          }}
          readOnly={!yeniMod || !yazabilir}
          searchable={yeniMod}
          data-testid="risk-isyeri"
        />
        <TextInput
          label="Değerlendirme adı"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
          readOnly={!yazabilir}
          data-testid="risk-ad"
        />
        {/* R1 (tur 11) — yöntem değerlendirmenin özelliğidir; KAYITTA DONAR. */}
        <Select
          label="Yöntem"
          description="Kayıtta dondurulur — sonradan değiştirilemez"
          data={RISK_METODOLOJILERI.map((m) => ({ value: m, label: RISK_METODOLOJI_ETIKETI[m] }))}
          value={metodoloji}
          onChange={(v) => {
            if (v === 'matrix_5x5' || v === 'fine_kinney') setMetodoloji(v);
          }}
          allowDeselect={false}
          readOnly={!yeniMod || !yazabilir}
          data-testid="risk-metodoloji"
        />
        <Select
          label="Kapsam"
          data={[
            { value: 'faaliyet', label: 'Faaliyet bazlı (bakım, işletme…)' },
            { value: 'lokasyon', label: 'Lokasyon/bölge bazlı' },
          ]}
          value={kapsamTipi}
          onChange={(v) => setKapsamTipi(v === 'lokasyon' ? 'lokasyon' : 'faaliyet')}
          allowDeselect={false}
          readOnly={!yazabilir}
          data-testid="risk-kapsam-tipi"
        />
        {kapsamTipi === 'lokasyon' ? (
          <Select
            label="Lokasyon"
            data={lokasyonlar}
            value={locationNodeId}
            onChange={setLocationNodeId}
            searchable
            readOnly={!yazabilir}
            data-testid="risk-lokasyon"
          />
        ) : (
          <TextInput
            label="Faaliyet adı"
            value={faaliyetAdi}
            onChange={(e) => setFaaliyetAdi(e.currentTarget.value)}
            readOnly={!yazabilir}
            data-testid="risk-faaliyet"
          />
        )}
      </SimpleGrid>
      {hata && (
        <Text c={ANLAM.engel} size="sm" mt="xs" data-testid="risk-hata">
          {hata}
        </Text>
      )}
      {yazabilir && (
        <Group justify="flex-end" mt="sm">
          <Button
            onClick={() => void gonder()}
            disabled={!kirli}
            loading={kaydet.isPending}
            data-testid="risk-kaydet"
          >
            {yeniMod ? 'Kaydet ve tabloyu aç' : 'Künyeyi kaydet'}
          </Button>
        </Group>
      )}
    </Bolum>
  );
}

/** Risk maddesinden DÖF: MEVCUT aksiyon formu alanları + `riskItemId`
 *  (olay tarafındaki `DofFormu` ile AYNI kapı — ikinci yol yazılmadı).
 *
 *  **Y2 / P2-b — ÖN DOLULUK MADDENİN DURUMUNDAN GELİR:** `dofOnerilir`
 *  (MEVCUT skordan, güncel eşikle — Z2 · R2) doğruysa başlık + açıklama ön
 *  dolu gelir; değilse boş.
 *
 *  ✏️ **MADDE 220 / R5 — AKSİYON HEDEFE BAĞLANIR:** ön dolu başlık
 *  **planlanan önlemdir**, açıklama **hedef skoru** taşır. Bağın kendisi yine
 *  `action.risk_item_id`dir (yeni kolon yok). */
function RiskDofFormu({
  madde,
  workplaceId,
  onClose,
}: {
  madde: RiskMaddesi;
  workplaceId: string;
  onClose: () => void;
}): React.JSX.Element {
  const ac = useAksiyonAc();
  const queryClient = useQueryClient();
  const [atanan, setAtanan] = useState<string | null>(null);
  const [kaynak, setKaynak] = useState<string | null>(null);
  // Z4 — ÖN DOLU METİN TASLAKTIR, İMZA DEĞİLDİR.
  const onDolu = madde.dofOnerilir;
  const hedefSatiri =
    madde.hedefHamSkor !== null
      ? `\nHedef skor: ${madde.hedefHamSkor}` +
        (madde.hedefRiskSeviyesi ? ` (${RISK_SEVIYESI_ETIKETI[madde.hedefRiskSeviyesi]})` : '')
      : '';
  const [baslik, setBaslik] = useState(onDolu ? (madde.ilaveOnlem ?? '') : '');
  const [termin, setTermin] = useState('');
  const [aciklama, setAciklama] = useState(
    onDolu
      ? `${madde.tehlike}${madde.tehlikeDigerMetin ? ` (${madde.tehlikeDigerMetin})` : ''} → ` +
          `${madde.risk}${madde.riskDigerMetin ? ` (${madde.riskDigerMetin})` : ''}` +
          (madde.mevcutOnlem ? `\nMevcut önlem: ${madde.mevcutOnlem}` : '') +
          hedefSatiri
      : '',
  );
  const [referansNotu, setReferansNotu] = useState('');
  // ✏️ T6/T13: KAYNAK SORULMUYOR — sunucu onu kayıttan türetiyor.
  const hazir = Boolean(atanan) && baslik.trim() !== '' && gunGirdisiIso(termin) !== null;
  return (
    <Stack data-testid="risk-dof-formu" data-on-dolu={onDolu ? 'evet' : 'hayir'}>
      <Text size="sm" c="dimmed">
        {madde.faaliyet} — {madde.tehlike} → {madde.risk}
      </Text>
      <AksiyonFormAlanlari
        atanan={atanan}
        setAtanan={setAtanan}
        kaynak={kaynak}
        setKaynak={setKaynak}
        kaynakTuretilir
        baslik={baslik}
        setBaslik={setBaslik}
        termin={termin}
        setTermin={setTermin}
        aciklama={aciklama}
        setAciklama={setAciklama}
        referansNotu={referansNotu}
        setReferansNotu={setReferansNotu}
      />
      {ac.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="risk-dof-hata">
          {ac.error.message}
        </Text>
      )}
      <Button
        data-testid="risk-dof-onayla"
        disabled={!hazir}
        loading={ac.isPending}
        onClick={() => {
          if (!hazir || !atanan) return;
          ac.mutate(
            {
              workplaceId,
              atananAppUserId: atanan,
              kaynakTipiId: null,
              baslik: baslik.trim(),
              terminTarihi: termin,
              riskItemId: madde.id,
              aciklama: aciklama.trim() === '' ? null : aciklama.trim(),
              kaynakReferansNotu: `Risk maddesi: ${madde.faaliyet}`,
            },
            {
              onSuccess: () => {
                // ⚠️ R7 — satırın sorumlu · termin · aksiyon hücreleri AKSİYONDAN
                // türetilir: aksiyon açılınca risk detayı da tazelenir, yoksa
                // satır yenilemeden eski hâlini gösterirdi (aksiyon tazeleyicisi
                // yalnız aksiyon anahtarlarını bilir).
                void queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
                onClose();
              },
            },
          );
        }}
      >
        Aksiyon aç
      </Button>
    </Stack>
  );
}

/**
 * Ölçek kademesi seçimi (FARK 5). Kademeler SUNUCUDAN gelir — ekran sayı
 * listesi UYDURMAZ; `NumberInput` bilinçli KULLANILMAZ, çünkü serbest sayı
 * yöntemin dışına çıkmanın kapısıdır.
 *
 * ✏️ **MADDE 220 / R9 — ETİKETLİ:** liste değer + anlamı birlikte gösterir
 * (*"6 — Yüksek, kuvvetle muhtemel"*); açılır liste hücreden geniş açılır ki
 * etiket okunabilsin (c-2: görünüyor · çalışıyor · kullanılamıyor olmasın).
 * Hücrede tek harflik yer tutucu yalnız boşken durur.
 */
function OlcekSecimi({
  etiket,
  kisaEtiket,
  kademeler,
  deger,
  secildi,
  testId,
}: {
  etiket: string;
  kisaEtiket: string;
  kademeler: readonly { deger: number; etiket: string }[] | undefined;
  deger: number | '';
  secildi: (v: number | '') => void;
  testId: string;
}): React.JSX.Element {
  return (
    <Select
      size="xs"
      className="isg-satir-olcek"
      placeholder={kisaEtiket}
      aria-label={etiket}
      data={(kademeler ?? []).map((k) => ({ value: String(k.deger), label: k.etiket }))}
      value={deger === '' ? null : String(deger)}
      onChange={(v) => secildi(v === null ? '' : Number(v))}
      comboboxProps={{ width: 'max-content', position: 'bottom-start' }}
      clearable
      data-testid={testId}
    />
  );
}

/**
 * RİSK MADDELERİ TOPLU YÜKLEME PANELİ (F4·S9).
 *
 * `PersonelYuklemePaneli` deseninin risk kolu — farkla: **önizleme YOK.**
 * Rapor satır numarası + sebep taşır. ✏️ **Madde 220:** başlığı uyuşmayan
 * (eski şablonla doldurulmuş) dosya istek düzeyinde reddedilir — mesaj
 * sunucudan gelir ve şablonu yeniden indirmeyi söyler.
 */
function RiskMaddeYuklemePaneli({ assessmentId }: { assessmentId: string }): React.JSX.Element {
  const yukle = useRiskMaddeYukle();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);
  return (
    <Stack gap="sm">
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="risk-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: `/api/risk-assessments/${assessmentId}/items/template`,
              yedekAd: 'risk-maddeleri-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="risk-madde-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={(secilen) => {
            setDosya(secilen);
            yukle.reset();
          }}
          clearable
          size="xs"
          w={300}
        />
        <Button
          size="xs"
          data-testid="risk-madde-yukle"
          disabled={!dosya}
          loading={yukle.isPending}
          onClick={() => dosya && yukle.mutate({ assessmentId, dosya })}
        >
          Yükle
        </Button>
      </Group>
      {yukle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="risk-yukleme-hata">
          {yukle.error.message}
        </Alert>
      )}
      {yukle.data && (
        <Stack gap="xs" data-testid="risk-yukleme-sonucu">
          <Group gap="xs">
            <Rozet anlam="olumlu">Eklendi: {yukle.data.eklenen}</Rozet>
            <Rozet anlam={yukle.data.hatali > 0 ? 'engel' : 'bilgi'}>
              İşlenemedi: {yukle.data.hatali}
            </Rozet>
          </Group>
          {yukle.data.hatali > 0 && (
            <Table striped data-testid="risk-yukleme-hatalari">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Satır</Table.Th>
                  <Table.Th>Sebep</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {yukle.data.satirlar
                  .filter((satir) => satir.sonuc === 'hata')
                  .map((satir) => (
                    <Table.Tr key={satir.satir} data-testid="risk-yukleme-hata-satiri">
                      <Table.Td>{satir.satir}</Table.Td>
                      <Table.Td>{satir.mesaj ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * ✏️ KA-3 (bulgu 8) — AKSİYONDAN TÜRETİLEN ÜÇ HÜCRE (R7). Elle giriş AÇILMAZ
 * (ikinci takip kaynağı olurdu); eksik olan boşluğun SEBEBİYDİ: üçü de "—"
 * gösterdiği için *aksiyon açılmadı* ile *alan kapalı* ayırt edilemiyordu.
 * Kayıtlı satır ve düzenleme satırı AYNI hücreleri çizer (düzenleme satırı
 * aksiyonu olan maddede de "—" gösteriyordu). İptal edilmiş aksiyon sayılmaz.
 */
function TakipHucreleri({
  madde,
  testOn,
}: {
  madde: RiskMaddesi | null;
  testOn: string;
}): React.JSX.Element {
  const aksiyonSayisi = madde?.aksiyonSayisi ?? 0;
  return (
    <>
      <Table.Td data-testid={`${testOn}-sorumlu`}>{madde?.sorumluAd ?? ''}</Table.Td>
      <Table.Td data-testid={`${testOn}-termin`}>
        {madde?.termin ? tarihGoster(madde.termin) : ''}
      </Table.Td>
      <Table.Td>
        {madde !== null && aksiyonSayisi > 0 ? (
          <Rozet anlam="bilgi" testId={`${testOn}-aksiyon-sayisi`}>
            {aksiyonSayisi} aksiyon
            {madde.acikAksiyonSayisi > 0 ? ` · ${madde.acikAksiyonSayisi} açık` : ''}
          </Rozet>
        ) : (
          <Text size="xs" c="dimmed" data-testid={`${testOn}-aksiyon-yok`}>
            Aksiyon yok
          </Text>
        )}
      </Table.Td>
    </>
  );
}
